/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext, useEffect, useState } from 'react';
import IEnergyManagementProps, {
  IAny
} from '../../../../DashboardKPIWidgets/IEnergyManagementProps';
import useConnectorAndDynamicProps from '../../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import useMounted from '../../../../shared/useMount/useMount';
import {
  getEnergyTabName,
  getIntervalType,
  getPortfolioVDConnectorConfig
} from '../../../../shared/Utils/PortfolioEnergy/PortfolioEnergyVDUtils';
import PortfolioEnergyBottomWidgetHOC from './BottomSection/PortfolioEnergyBottomWidgetHOC';
import PortfolioEnergyPremiumBottomWidgetHOC from './BottomSection/PortfolioEnergyPremiumBottomWidgetHOC';
import PortfolioEnergyTopWidgetHOC from './TopSection/PortfolioEnergyTopWidgetHOC';
import moment from 'moment';
import {
  getEnergyEndDateBasedOnSub,
  getEnergyStartDate
} from '../../../../shared/Utils/EnergyUsagesViewDetailsUtils';
import NoData from '../../../../shared/ErrorComponents/NoData';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../../locales/en-US/descriptors';
import {
  IntervalType,
  portfolioEnergyTabs
} from '../../../../shared/Utils/Constants';
import { ApiContext } from '../../../../../context/ApiContext';
import PortfolioEnergyTopWidgetHOCSeprate from './TopSection/PortfolioEnergyTopWidgetHOCSeprate';
import {
  EmaasSubscriptionType,
  EnergyUsageDataSourceType
} from '../../../../shared/Enums';
import PortfolioEnergyBottomWidgetHOCSeparate from './BottomSection/PortfolioEnergyBottomWidgetHOCSeparate';

const PortfolioEnergyAllUtilitiesVD = (props: IEnergyManagementProps) => {
  const {
    bearerToken,
    requestContext,
    utilityType,
    emaasContext,
    dataSourceInitial,
    storeDataSourceType
  } = props;
  const startDate = getEnergyStartDate(
    requestContext.context.startTime!,
    requestContext.context.subscriptionType!,
    undefined,
    undefined,
    requestContext.context.dataSource ===
      EnergyUsageDataSourceType.UTILITY_METER
  );
  const endDate = getEnergyEndDateBasedOnSub(
    requestContext.context.subscriptionType!,
    requestContext.context.endTime!,
    requestContext.context.startTime!,
    undefined,
    undefined,
    requestContext.context.dataSource ===
      EnergyUsageDataSourceType.UTILITY_METER
  );
  const apiContext = useContext(ApiContext);
  const enableAPISeparation =
    requestContext.context.subscriptionType ===
      EmaasSubscriptionType.PREMIUM_SUBSCRIPTION &&
    apiContext.toggleSeparateApi &&
    requestContext?.context?.dataSource !==
      EnergyUsageDataSourceType.UTILITY_METER;

  const [openmodal, setOpenmodal] = useState(false);

  const [dataSourceType, setDataSourceType] = React.useState<
    EnergyUsageDataSourceType | string | null
  >(
    emaasContext?.enableConsumptionSourceSelect
      ? dataSourceInitial
        ? dataSourceInitial
        : null
      : null
  );

  const connectorConfig = !enableAPISeparation
    ? getPortfolioVDConnectorConfig(requestContext, utilityType!)
    : {
        name: 'emaas-energy-usage-summary',
        options: {
          startDate: startDate,
          endDate: endDate,
          allDetails: 'true',
          utilityType: utilityType!,
          intervalType: getIntervalType(startDate, endDate),
          dataSource: requestContext?.context?.dataSource || ''
        }
      };

  const connectorConfigPerformanceAggregation = {
    name: 'emaas-energy-usage-performance-aggregation',
    options: {
      startDate: startDate,
      endDate: endDate,
      allDetails: 'true',
      utilityType: utilityType!,
      intervalType:
        utilityType === portfolioEnergyTabs.Water
          ? IntervalType.Monthly
          : getIntervalType(startDate, endDate),
      dataSource: requestContext?.context?.dataSource || ''
    }
  };

  const connectorConfigTable = {
    name: 'emaas-energy-usage-table',
    options: {
      startDate: startDate,
      endDate: endDate,
      allDetails: 'true',
      utilityType: utilityType!,
      groupType: 'Site',
      intervalType:
        utilityType === portfolioEnergyTabs.Water
          ? IntervalType.Monthly
          : getIntervalType(startDate, endDate),
      dataSource: requestContext?.context?.dataSource || ''
    }
  };

  const [dataConnectorPerformanceAggregation] = useConnectorAndDynamicProps(
    {
      connectorConfig: connectorConfigPerformanceAggregation,
      requestContext,
      bearerToken
    },
    enableAPISeparation
  );

  const [dataConnectorTable] = useConnectorAndDynamicProps(
    {
      connectorConfig: connectorConfigTable,
      requestContext,
      bearerToken
    },
    enableAPISeparation
  );
  const [t] = useTranslation();
  const d = descriptors.energyManagement;

  const [isOutOfRange, setIsOutOfRange] = useState<boolean>(false);

  const isMounted = useMounted();

  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });

  const isDateRangeValid = () => {
    const sDate = moment(
      getEnergyStartDate(
        requestContext.context.startTime!,
        requestContext.context.subscriptionType!
      ),
      'YYYY-MM-DD'
    );
    const eDate = moment(
      getEnergyEndDateBasedOnSub(
        requestContext.context.subscriptionType!,
        requestContext.context.endTime!,
        requestContext.context.startTime!
      ),
      'YYYY-MM-DD'
    );
    return eDate.diff(sDate, 'days') <= 365;
  };

  useEffect(() => {
    if (isMounted) {
      if (isDateRangeValid()) {
        setIsOutOfRange(false);
        dataConnector &&
          dataConnector.actions &&
          dataConnector.actions.getAll();
        if (enableAPISeparation) {
          dataConnectorPerformanceAggregation &&
            dataConnectorPerformanceAggregation.actions &&
            dataConnectorPerformanceAggregation.actions.getAll();
          dataConnectorTable &&
            dataConnectorTable.actions &&
            dataConnectorTable.actions.getAll();
        }
      } else {
        setIsOutOfRange(true);
      }
    }
  }, [requestContext, enableAPISeparation]);

  useEffect(() => {
    if (!isDateRangeValid()) {
      setIsOutOfRange(true);
    }
  }, []);

  const isSitePremium =
    requestContext.context.subscriptionType ===
      EmaasSubscriptionType.PREMIUM_SUBSCRIPTION &&
    apiContext.toggleSeparateApi;

  return isOutOfRange ? (
    <div className="noDataDisplay common-error-container">
      <NoData message={t(d.UTILITYYEAROUTOFRANGE)} />
    </div>
  ) : (
    <div className="all-utilities-details porttfolio-energy-vd">
      {enableAPISeparation ? (
        <PortfolioEnergyTopWidgetHOCSeprate
          dataConnector={dataConnector}
          dataSourceType={dataSourceType}
          dataConnectorPerformanceAggregation={
            dataConnectorPerformanceAggregation
          }
          tabName={getEnergyTabName(utilityType!)}
        />
      ) : (
        <PortfolioEnergyTopWidgetHOC
          dataConnector={dataConnector}
          tabName={getEnergyTabName(utilityType!)}
        />
      )}

      {enableAPISeparation ? (
        <PortfolioEnergyBottomWidgetHOCSeparate
          dataConnector={dataConnectorTable}
          tabName={getEnergyTabName(utilityType!)}
        />
      ) : (
        <PortfolioEnergyBottomWidgetHOC
          dataConnector={dataConnector}
          tabName={getEnergyTabName(utilityType!)}
        />
      )}
    </div>
  );
};
export default PortfolioEnergyAllUtilitiesVD;
